/**
 * HRMailTemplateManagement — Admin
 * Configuration mail par structure (arrêt de travail) + bibliothèque de templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Edit, Trash2, Save, Check, Loader, X, Copy } from 'lucide-react';
import { ConfirmModal } from '../ui';
import {
  getStructureIdentity, upsertStructureIdentity,
  getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate,
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';
import { useStructure } from '../../contexts/StructureContext';

const VARIABLES_HINT = '{{prenom}} {{nom}} {{poste}} {{contrat}} {{dateDebut}} {{dateFin}} {{structure}}';

const EMPTY_CONFIG = {
  hrAgencyEmail: '',
  hrEmailSubject: '',
  hrEmailBody: '',
};

const EMPTY_TEMPLATE = { name: '', subject: '', body: '' };

const DEFAULT_SUBJECT = '[SuiviPlus] Arrêt de travail — {{prenom}} {{nom}} — du {{dateDebut}} au {{dateFin}}';
const DEFAULT_BODY = `Madame, Monsieur,\n\nVeuillez trouver ci-joint l'arrêt de travail de {{prenom}} {{nom}} ({{poste}}), en arrêt maladie du {{dateDebut}} au {{dateFin}}.\n\nMerci de bien vouloir en prendre note et d'effectuer les démarches nécessaires auprès des organismes compétents.\n\nCordialement,`;

// ─── Aide variables ───────────────────────────────────────────────────────────

const VarsHint = () => (
  <p className="mt-1.5 text-xs text-gray-400">
    Variables disponibles :{' '}
    {VARIABLES_HINT.split(' ').map(v => (
      <code key={v} className="bg-gray-100 px-1 rounded mr-1">{v}</code>
    ))}
    <br />La signature (nom structure, adresse, téléphone) est ajoutée automatiquement.
  </p>
);

// ─── Formulaire template (création / édition) ─────────────────────────────────

const TemplateForm = ({ initial, structureId, structures, onSave, onClose }) => {
  const [form, setForm] = useState(initial || EMPTY_TEMPLATE);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error('Nom, objet et corps sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, structureId: form.structureId ?? null };
      if (initial?.id) {
        await updateEmailTemplate(initial.id, payload);
      } else {
        await createEmailTemplate({ ...payload, structureId: structureId || null });
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur sauvegarde');
    }
    setSaving(false);
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">
            {initial?.id ? 'Modifier le template' : 'Nouveau template mail'}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nom du template <span className="text-red-500">*</span></label>
            <input className={inputCls} placeholder="Ex: Arrêt de travail standard" value={form.name}
              onChange={e => set('name', e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Portée</label>
            <select className={inputCls} value={form.structureId || ''} onChange={e => set('structureId', e.target.value || null)}>
              <option value="">Global (toutes structures)</option>
              {structures.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Objet <span className="text-red-500">*</span></label>
            <input className={inputCls}
              placeholder={DEFAULT_SUBJECT}
              value={form.subject}
              onChange={e => set('subject', e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Corps <span className="text-red-500">*</span></label>
            <textarea className={`${inputCls} resize-y`} rows={8}
              placeholder={DEFAULT_BODY}
              value={form.body}
              onChange={e => set('body', e.target.value)} />
            <VarsHint />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            {initial?.id ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale config arrêt de travail ──────────────────────────────────────────

const ArretDeTravailModal = ({ structureId, onClose }) => {
  const toast = useToast();
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  useEffect(() => {
    if (!structureId) return;
    setLoading(true);
    getStructureIdentity(structureId)
      .then(res => {
        const d = res.data || {};
        setConfig({ hrAgencyEmail: d.hrAgencyEmail || '', hrEmailSubject: d.hrEmailSubject || '', hrEmailBody: d.hrEmailBody || '' });
      })
      .catch(() => setConfig(EMPTY_CONFIG))
      .finally(() => setLoading(false));
  }, [structureId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertStructureIdentity(structureId, config);
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 1000);
      toast.success('Configuration enregistrée');
    } catch {
      toast.error('Erreur enregistrement');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">Arrêt de travail — Configuration par défaut</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><X size={16} /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader className="animate-spin text-primary" size={20} /></div>
        ) : (
          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Adresse cabinet RH
                <span className="ml-1 text-gray-400 font-normal">(destinataire automatique)</span>
              </label>
              <input type="email" className={inputCls} placeholder="contact@cabinet-rh.fr"
                value={config.hrAgencyEmail} onChange={e => setConfig(c => ({ ...c, hrAgencyEmail: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Objet <span className="text-gray-400 font-normal">(laisser vide pour le modèle par défaut)</span>
              </label>
              <input type="text" className={inputCls} placeholder={DEFAULT_SUBJECT}
                value={config.hrEmailSubject} onChange={e => setConfig(c => ({ ...c, hrEmailSubject: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Corps <span className="text-gray-400 font-normal">(laisser vide pour le modèle par défaut)</span>
              </label>
              <textarea className={`${inputCls} resize-y`} rows={6} placeholder={DEFAULT_BODY}
                value={config.hrEmailBody} onChange={e => setConfig(c => ({ ...c, hrEmailBody: e.target.value }))} />
              <VarsHint />
            </div>
          </div>
        )}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Annuler</button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
            {saving ? <Loader size={14} className="animate-spin" /> : saved ? <Check size={14} /> : <Save size={14} />}
            {saved ? 'Enregistré' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const HRMailTemplateManagement = () => {
  const toast = useToast();
  const { currentStructure, structures } = useStructure();
  const structureId = currentStructure?.id || null;

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showArretModal, setShowArretModal] = useState(false);
  const [deleteTemplateConfirm, setDeleteTemplateConfirm] = useState(null);

  // Chargement config (pour afficher l'email destinataire sur la carte)
  useEffect(() => {
    if (!structureId) return;
    getStructureIdentity(structureId)
      .then(res => {
        const d = res.data || {};
        setConfig({ hrAgencyEmail: d.hrAgencyEmail || '', hrEmailSubject: d.hrEmailSubject || '', hrEmailBody: d.hrEmailBody || '' });
      })
      .catch(() => setConfig(EMPTY_CONFIG));
  }, [structureId]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await getEmailTemplates(structureId || undefined);
      setTemplates(res.data || []);
    } catch {
      toast.error('Erreur chargement templates');
    } finally { setLoadingTemplates(false); }
  }, [structureId, toast]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleDeleteTemplate = (id) => setDeleteTemplateConfirm(id);

  const doDeleteTemplate = async () => {
    const id = deleteTemplateConfirm;
    setDeleteTemplateConfirm(null);
    try {
      await deleteEmailTemplate(id);
      toast.success('Template supprimé');
      loadTemplates();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleDuplicateTemplate = async (tpl) => {
    try {
      await createEmailTemplate({ name: `${tpl.name} (copie)`, subject: tpl.subject, body: tpl.body, structureId: tpl.structureId });
      toast.success('Template dupliqué');
      loadTemplates();
    } catch {
      toast.error('Erreur duplication');
    }
  };

  if (!currentStructure) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        Sélectionnez une structure dans l'en-tête pour accéder à la configuration mail.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            <Mail size={15} className="text-gray-400" />
            <div>
              <h2 className="text-sm font-semibold text-gray-700">Bibliothèque de templates</h2>
              <p className="text-xs text-gray-400">Templates réutilisables pour les envois mail RH</p>
            </div>
          </div>
          <button
            onClick={() => { setEditingTemplate(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark transition-colors"
          >
            <Plus size={14} /> Nouveau template
          </button>
        </div>

        <ul className="divide-y divide-gray-50">
          {/* ── Carte épinglée : Arrêt de travail ── */}
          <li className="px-5 py-3 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-gray-800">Arrêt de travail</span>
                  <span className="px-1.5 py-0.5 text-xs bg-accent/20 text-accent-dark rounded">Par défaut</span>
                  <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">{currentStructure.name}</span>
                </div>
                <p className="text-xs text-gray-500 truncate">{config.hrEmailSubject || DEFAULT_SUBJECT}</p>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 whitespace-pre-line">{config.hrEmailBody || DEFAULT_BODY}</p>
                {config.hrAgencyEmail && (
                  <p className="text-xs text-gray-400 mt-1">→ {config.hrAgencyEmail}</p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => setShowArretModal(true)}
                  className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg" title="Modifier">
                  <Edit size={14} />
                </button>
              </div>
            </div>
          </li>

          {/* ── Templates créés ── */}
          {loadingTemplates ? (
            <li className="flex justify-center py-8"><Loader className="animate-spin text-primary" size={20} /></li>
          ) : templates.length === 0 ? (
            <li className="py-8 text-center">
              <p className="text-sm text-gray-400">Aucun template créé</p>
              <p className="text-xs text-gray-300 mt-1">Créez des modèles réutilisables pour vos communications RH</p>
            </li>
          ) : templates.map(tpl => (
            <li key={tpl.id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-gray-800">{tpl.name}</span>
                    {tpl.structureId ? (
                      <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded">
                        {structures.find(s => s.id === tpl.structureId)?.name || 'Structure'}
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">Global</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{tpl.subject}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 whitespace-pre-line">{tpl.body}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleDuplicateTemplate(tpl)}
                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg" title="Dupliquer">
                    <Copy size={14} />
                  </button>
                  <button onClick={() => { setEditingTemplate(tpl); setShowForm(true); }}
                    className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg" title="Modifier">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDeleteTemplate(tpl.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Supprimer">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {showForm && (
        <TemplateForm
          initial={editingTemplate}
          structureId={structureId}
          structures={structures}
          onSave={() => { setShowForm(false); setEditingTemplate(null); loadTemplates(); }}
          onClose={() => { setShowForm(false); setEditingTemplate(null); }}
        />
      )}

      <ConfirmModal
        open={!!deleteTemplateConfirm}
        title="Supprimer ce template ?"
        message="Cette action est irréversible."
        onConfirm={doDeleteTemplate}
        onCancel={() => setDeleteTemplateConfirm(null)}
        danger
      />

      {showArretModal && (
        <ArretDeTravailModal
          structureId={structureId}
          onClose={() => {
            setShowArretModal(false);
            getStructureIdentity(structureId).then(res => {
              const d = res.data || {};
              setConfig({ hrAgencyEmail: d.hrAgencyEmail || '', hrEmailSubject: d.hrEmailSubject || '', hrEmailBody: d.hrEmailBody || '' });
            }).catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default HRMailTemplateManagement;
